import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { CampusArea, CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { CampusSubject } from '@chancenwerk/db/enums/CampusSubject.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap, takeUntil } from 'rxjs';

/**
 * @customElement campus-dialog-edit-section
 */
@customElement('campus-dialog-edit-section')
export class CampusDialogEditSection extends ChromiumDialog<void> {
  @property({ attribute: false }) public area = new RXController(this, '');

  @state() public section = new RXController(this, new CampusSection({}));

  @state() private readonly _sectionIsDirty = new RXController(
    this,
    this.section.observable.pipe(switchMap(section => section.dirty)),
  );

  private readonly _form = new FormController(this, this.section.observable, {
    success: async section => this._handleFormSuccess(section),
    error: err => this._handleFormError(err),
  });

  static override get element_name() {
    return 'campus-dialog-edit-section';
  }

  override connectedCallback() {
    super.connectedCallback();
    this.section.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => (this.heading = !e._id ? 'Themenbereich erstellen' : 'Themenbereich bearbeiten'));

    this.area.observable
      .pipe(takeUntil(this.disconnected), filter(Boolean))
      .subscribe(area => (this.section.value.area = area));
  }

  private async _handleFormSuccess(section: CampusSection) {
    // succes form create for new object or save on existing object
    if (!section._id) await section.create().catch(err => console.error(err.message));
    else await section.save().catch(err => console.error(err.message));
    void ChromiumSnackbar.show('Themenbereich wurde gespeichert');
    await this.close();
  }
  private _handleFormError(err: ValidationError<any>) {
    console.error(err.message);
  }

  public override content() {
    return html`
      <cr-flex space="md" padding="md">
        <cr-input ${this._form.field('name')} placeholder="Name des Themenbereichs"></cr-input>
        <cr-checkbox ${this._form.field('hiddenFromStudents')} label="Themenbereich verstecken?"></cr-checkbox>
        ${this.section.value.area === CampusArea.STUDENTS
          ? html`<cr-dropdown-multiselection
                ${this._form.field('forGrades')}
                placeholder="Jahrgangsstufen"
                .selectables=${ChancenwerkUser.availableGrades}
              ></cr-dropdown-multiselection>
              <cr-dropdown-multiselection
                ${this._form.field('subjects')}
                placeholder="Schulfächer"
                .selectables=${Object.values(CampusSubject)}
              ></cr-dropdown-multiselection>`
          : nothing}

        <cr-flex horizontal crossaxis-center end space="md">
          ${this.section.value._id
            ? html`
                <campus-delete-button
                  @click="${async () => {
                    if (!(await ChromiumDialog.confirm('Themenbereich wirklich löschen?'))) return;
                    await this.section.value.remove();
                    void ChromiumSnackbar.show('Themenbereich wurde gelöscht');
                    await this.close();
                  }}"
                >
                </campus-delete-button>
              `
            : nothing}
          ${this._sectionIsDirty.value || !this.section.value._id
            ? html` <cr-button mode="outline" @click=${() => this.close()}> Abbrechen </cr-button> `
            : nothing}
          <cr-button @click=${() => this._form.validate()}>
            ${!this.section.value._id ? html` Erstellen ` : html` Speichern `}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  public override async cancel() {
    if (this._sectionIsDirty.value && this.section.value._id) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      if (this.section.value._id) this.section.value.revert();
    }
    await super.cancel();
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);
    if (!options.draggable) {
      wrapperElem.style.setProperty('max-width', '40vw');
      wrapperElem.style.setProperty('min-width', '40vw');
    }
    return wrapperElem;
  }
}
